import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';

export type ReferralType = 'contact' | 'deal';

export interface Referral {
  id: string;
  referrer_contact_id: string;
  referred_contact_id: string;
  referral_type: ReferralType;
  deal_id?: string | null;
  context?: 'family' | 'friend' | 'coworker' | 'partner' | 'other';
  notes?: string;
  revenue_share_percentage?: number;
  is_active?: boolean;
  deactivated_at?: string | null;
  deactivation_reason?: string | null;
  created_at?: string;
  referred_contact?: {
    id: string;
    first_name: string;
    last_name?: string;
    primary_email?: string;
    primary_phone?: string;
  };
  referrer_contact?: {
    id: string;
    first_name: string;
    last_name?: string;
    primary_email?: string;
    primary_phone?: string;
  };
  deal?: {
    id: string;
    stage: string;
    total_amount?: number;
    created_at?: string;
  };
}

export interface ReferrerRevenue {
  referrer_contact_id: string;
  referrer_name: string;
  total_referrals: number;
  total_deals_from_referrals: number;
  total_deal_value: number;
  total_revenue_received: number;
  first_referral_at: string;
}

export interface CreateContactWithReferralData {
  first_name: string;
  last_name?: string;
  primary_phone?: string;
  primary_email?: string;
  context?: 'family' | 'friend' | 'coworker' | 'partner' | 'other';
  sales_development_id?: string;
  sales_consultant_id?: string;
}

export interface DuplicateCheckResult {
  exists: boolean;
  contact?: {
    id: string;
    first_name: string;
    last_name?: string;
    primary_email?: string;
    primary_phone?: string;
  };
}

export function useReferrals(contactId?: string) {
  const queryClient = useQueryClient();

  // Referrals de CONTATO feitos por este contato (quem ele indicou como contato novo)
  const { data: contactReferralsMade = [], isLoading: isLoadingContactReferrals } = useQuery({
    queryKey: ['referrals-contact-made', contactId],
    queryFn: async () => {
      if (!contactId) return [];
      
      const { data, error } = await supabase
        .from('referrals')
        .select(`
          *,
          referred_contact:contacts!referred_contact_id(
            id,
            first_name,
            last_name,
            primary_email,
            primary_phone
          )
        `)
        .eq('referrer_contact_id', contactId)
        .eq('referral_type', 'contact')
        .order('created_at', { ascending: false });

      if (error) throw error;
      return data as Referral[];
    },
    enabled: !!contactId,
  });

  // Referrals de DEAL feitos por este contato (deals específicos que ele indicou)
  const { data: dealReferralsMade = [], isLoading: isLoadingDealReferrals } = useQuery({
    queryKey: ['referrals-deal-made', contactId],
    queryFn: async () => {
      if (!contactId) return [];
      
      const { data, error } = await supabase
        .from('referrals')
        .select(`
          *,
          referred_contact:contacts!referred_contact_id(
            id,
            first_name,
            last_name,
            primary_email,
            primary_phone
          ),
          deal:deals!deal_id(
            id,
            stage,
            total_amount,
            created_at
          )
        `)
        .eq('referrer_contact_id', contactId)
        .eq('referral_type', 'deal')
        .order('created_at', { ascending: false });

      if (error) throw error;
      return data as Referral[];
    },
    enabled: !!contactId,
  });

  // TODOS os referrals feitos por este contato (para compatibilidade)
  const referralsMade = [...contactReferralsMade, ...dealReferralsMade];

  // Quem indicou este contato (referredBy) - apenas type = 'contact'
  const { data: referredBy, isLoading: isLoadingReferredBy } = useQuery({
    queryKey: ['referred-by', contactId],
    queryFn: async () => {
      if (!contactId) return null;
      
      const { data, error } = await supabase
        .from('referrals')
        .select(`
          *,
          referrer_contact:contacts!referrer_contact_id(
            id,
            first_name,
            last_name,
            primary_email,
            primary_phone
          )
        `)
        .eq('referred_contact_id', contactId)
        .eq('referral_type', 'contact')
        .maybeSingle();

      if (error) throw error;
      return data as Referral | null;
    },
    enabled: !!contactId,
  });

  // Revenue gerado pelas indicações deste contato
  const { data: referrerRevenue } = useQuery({
    queryKey: ['referrer-revenue', contactId],
    queryFn: async () => {
      if (!contactId) return null;
      
      const { data, error } = await supabase
        .from('v_referrer_revenue')
        .select('*')
        .eq('referrer_contact_id', contactId)
        .maybeSingle();

      if (error) throw error;
      return data as ReferrerRevenue | null;
    },
    enabled: !!contactId,
  });

  // Verifica se contato já existe pelo telefone ou email
  const checkDuplicateContact = async (phone?: string, email?: string): Promise<DuplicateCheckResult> => {
    if (!phone && !email) return { exists: false };

    let query = supabase
      .from('contacts')
      .select('id, first_name, last_name, primary_email, primary_phone');

    if (phone && email) {
      query = query.or(`primary_phone.eq.${phone},primary_email.eq.${email}`);
    } else if (phone) {
      query = query.eq('primary_phone', phone);
    } else if (email) {
      query = query.eq('primary_email', email);
    }

    const { data, error } = await query.limit(1).maybeSingle();

    if (error) {
      console.error('Error checking duplicate:', error);
      return { exists: false };
    }

    return {
      exists: !!data,
      contact: data || undefined,
    };
  };

  const createReferral = useMutation({
    mutationFn: async (referral: Omit<Referral, 'id' | 'created_at'>) => {
      const { data, error } = await supabase
        .from('referrals')
        .insert({
          ...referral,
          referral_type: referral.referral_type || 'contact',
        })
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['referrals-contact-made', contactId] });
      queryClient.invalidateQueries({ queryKey: ['referrals-deal-made', contactId] });
      queryClient.invalidateQueries({ queryKey: ['referred-by', contactId] });
      queryClient.invalidateQueries({ queryKey: ['referrer-revenue', contactId] });
      toast.success('Indicação registrada');
    },
    onError: (error: any) => {
      if (error?.code === '23505' || error?.message?.includes('unique_referred')) {
        toast.error('Este contato já foi indicado por outra pessoa');
      } else {
        toast.error('Erro ao registrar indicação');
      }
      console.error('Error creating referral:', error);
    },
  });

  // Criar referral de deal (quando contato já existe)
  const createDealReferral = useMutation({
    mutationFn: async ({ 
      referrerContactId,
      referredContactId,
      dealId,
      context,
    }: { 
      referrerContactId: string;
      referredContactId: string;
      dealId: string;
      context?: string;
    }) => {
      const { data, error } = await supabase
        .from('referrals')
        .insert({
          referrer_contact_id: referrerContactId,
          referred_contact_id: referredContactId,
          deal_id: dealId,
          referral_type: 'deal',
          context: context || 'other',
          is_active: true,
          revenue_share_percentage: 10,
        })
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['referrals-deal-made', contactId] });
      queryClient.invalidateQueries({ queryKey: ['referrer-revenue', contactId] });
      toast.success('Indicação de deal registrada');
    },
    onError: (error: any) => {
      console.error('Error creating deal referral:', error);
      toast.error('Erro ao registrar indicação de deal');
    },
  });

  // Criar contato + referral + deal (se não tiver deals) em uma operação
  const createContactWithReferral = useMutation({
    mutationFn: async ({ 
      contactData, 
      referrerContactId 
    }: { 
      contactData: CreateContactWithReferralData; 
      referrerContactId: string;
    }) => {
      // 1. Criar o contato com source = 'Customer Referral'
      const { data: newContact, error: contactError } = await supabase
        .from('contacts')
        .insert({
          first_name: contactData.first_name,
          last_name: contactData.last_name || null,
          primary_phone: contactData.primary_phone || null,
          primary_email: contactData.primary_email || null,
          contacts_source: 'Customer Referral',
        })
        .select()
        .single();

      if (contactError) throw contactError;

      // 2. Criar o referral (type = 'contact')
      const { data: referral, error: referralError } = await supabase
        .from('referrals')
        .insert({
          referrer_contact_id: referrerContactId,
          referred_contact_id: newContact.id,
          referral_type: 'contact',
          context: contactData.context || null,
          is_active: true,
          revenue_share_percentage: 10,
        })
        .select()
        .single();

      if (referralError) {
        // Se falhar, deletar o contato criado
        await supabase.from('contacts').delete().eq('id', newContact.id);
        throw referralError;
      }

      // 3. Verificar se tem SDR ou Consultor para criar deal
      const hasSdrOrConsultant = contactData.sales_development_id || contactData.sales_consultant_id;
      
      if (hasSdrOrConsultant) {
        // Determinar stage: scheduled se tem consultor, new se não
        const dealStage = contactData.sales_consultant_id ? 'scheduled' : 'new';
        
        // 4. Criar o deal com source = 'Customer Referral'
        const { data: newDeal, error: dealError } = await supabase
          .from('deals')
          .insert({
            contact_id: newContact.id,
            deals_source: 'Customer Referral',
            source: 'direct',
            stage: dealStage,
            referred_by_contact_id: referrerContactId,
            sales_development: contactData.sales_development_id || null,
            sales_consultant: contactData.sales_consultant_id || null,
          })
          .select('id')
          .single();

        if (dealError) {
          console.error('Error creating deal for new referral:', dealError);
          // Não falha a operação, apenas loga o erro
        } else if (newDeal) {
          // 5. Criar referral de deal também
          await supabase
            .from('referrals')
            .insert({
              referrer_contact_id: referrerContactId,
              referred_contact_id: newContact.id,
              deal_id: newDeal.id,
              referral_type: 'deal',
              context: contactData.context || 'other',
              is_active: true,
              revenue_share_percentage: 10,
            });

          return { contact: newContact, referral, deal: newDeal };
        }
      }

      return { contact: newContact, referral, deal: null };
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['referrals-contact-made', contactId] });
      queryClient.invalidateQueries({ queryKey: ['referrals-deal-made', contactId] });
      queryClient.invalidateQueries({ queryKey: ['referrer-revenue', contactId] });
      queryClient.invalidateQueries({ queryKey: ['contacts'] });
      queryClient.invalidateQueries({ queryKey: ['deals'] });
      
      if (data.deal) {
        toast.success('Contato, deal e indicação criados com sucesso');
      } else {
        toast.success('Contato criado e indicação registrada');
      }
    },
    onError: (error: any) => {
      console.error('Error creating contact with referral:', error);
      
      // Verificar se é erro de duplicata do TRIGGER
      const triggerMatch = error?.message?.match(/DUPLICATE_PHONE_TRIGGER::([a-f0-9-]+)::/i);
      if (triggerMatch) {
        const duplicateContactId = triggerMatch[1];
        throw { 
          type: 'DUPLICATE_TRIGGER', 
          contactId: duplicateContactId,
          originalMessage: error.message 
        };
      }
      
      if (error?.code === '23505') {
        toast.error('Este contato já foi indicado por outra pessoa');
      } else {
        toast.error('Erro ao criar contato');
      }
    },
  });

  return {
    // Separados por tipo
    contactReferralsMade,
    dealReferralsMade,
    // Todos juntos (compatibilidade)
    referralsMade,
    referredBy,
    referrerRevenue,
    isLoading: isLoadingContactReferrals || isLoadingDealReferrals || isLoadingReferredBy,
    // Mutations
    createReferral: createReferral.mutateAsync,
    createDealReferral: createDealReferral.mutateAsync,
    createContactWithReferral: createContactWithReferral.mutateAsync,
    checkDuplicateContact,
  };
}

// Hook para top referrers (dashboard/analytics)
export function useTopReferrers(limit = 10) {
  return useQuery({
    queryKey: ['top-referrers', limit],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('v_referrer_revenue')
        .select('*')
        .order('total_revenue_received', { ascending: false })
        .limit(limit);

      if (error) throw error;
      return data as ReferrerRevenue[];
    },
  });
}
